/**
 * Guided onboarding wizard — step definitions and upload mapping.
 */

export const ONBOARDING_WIZARD_STEPS = [
  {
    key: 'welcome',
    title: 'Welcome',
    subtitle: 'Get your organization live in a few guided steps',
    type: 'welcome',
  },
  {
    key: 'system_setup',
    title: 'System setup',
    subtitle: 'One-click reference data',
    type: 'setup',
    progressKey: 'system_setup',
  },
  {
    key: 'members',
    title: 'Customer accounts',
    subtitle: 'Import individual or group accounts',
    type: 'upload',
    progressKey: 'members',
    uploadTypes: [
      { value: 'singlemembers', label: 'Individual customers' },
      { value: 'groups', label: 'Group accounts' },
    ],
    tip: 'System setup runs automatically on this step. All ledger accounts and profile links are created for you.',
  },
  {
    key: 'opening_balances',
    title: 'Opening balances',
    subtitle: 'Deposits, shares & ledger balances',
    type: 'upload',
    progressKey: 'opening_balances',
    optional: true,
    uploadTypes: [
      { value: 'deposits', label: 'Opening deposits (recommended)' },
      { value: 'coa_opening_balances', label: 'COA / trial balance opening balances' },
      { value: 'multitransactions', label: 'Multi-transaction opening balances' },
    ],
    tip: 'Customer accounts must exist. Use deposits for member savings/shares; use COA opening balances for other GL accounts (equipment, payables, equity, etc.). Date format: DD/MM/YYYY.',
  },
  {
    key: 'loans',
    title: 'Running loans',
    subtitle: 'Outstanding loan portfolio',
    type: 'loans',
    progressKey: 'loans',
    optional: true,
    uploadTypes: [{ value: 'loans', label: 'Running loans' }],
    tip: 'For loans paid part-way: use Path A in the guide below (Amount = original, Principle/Interest balance = still owed).',
    extraLinks: [
      { to: '/loanopening_balances_upload/', label: 'Fix balances after import (Path C)' },
      { to: '/dataonboarding_json/', label: 'Member JSON imports (schedules, assessments)' },
    ],
  },
  {
    key: 'loan_schedules',
    title: 'Loan schedules',
    subtitle: 'Exact installment dates from old system',
    type: 'upload',
    progressKey: 'loan_schedules',
    optional: true,
    uploadTypes: [{ value: 'loanschedules', label: 'Loan installment schedules' }],
    tip: 'Optional but recommended: import your old amortization table after running loans. Replaces auto-generated installments with your real dates and amounts.',
  },
  {
    key: 'loan_repayments',
    title: 'Loan repayments',
    subtitle: 'Payment history (Path B only)',
    type: 'upload',
    progressKey: 'loan_repayments',
    optional: true,
    uploadTypes: [{ value: 'loanrepayments', label: 'Loan repayments' }],
    tip: 'Only if you imported loans with zero balances and need each historical payment on record. Skip if you used Principle/Interest balance on running loans.',
  },
  {
    key: 'ledger_history',
    title: 'Expenses & incomes',
    subtitle: 'Non-loan ledger history',
    type: 'upload',
    progressKey: 'ledger_history',
    optional: true,
    uploadTypes: [
      { value: 'expenses', label: 'Expenses' },
      { value: 'incomes', label: 'Other incomes' },
    ],
    uploadTypesExtra: [{ value: 'chartofaccounts', label: 'Extra chart of accounts' }],
  },
  {
    key: 'staff_users',
    title: 'Staff users',
    subtitle: 'Staff who will operate the system',
    type: 'users',
    progressKey: 'staff_users',
    optional: true,
    uploadTypes: [{ value: 'system_users', label: 'Import system users' }],
    manualLink: { to: '/useraccounts/', label: 'Create users manually under User Accounts' },
  },
  {
    key: 'advanced_json',
    title: 'Advanced',
    subtitle: 'Per-member JSON migration',
    type: 'advanced',
    optional: true,
    tip: 'Use when one customer needs full journal/statement/loan workflow history beyond Excel templates.',
    links: [
      { to: '/dataonboarding_json/', label: 'Member JSON import tool' },
      { to: '/loanopening_balances_upload/', label: 'Loan opening balance corrections (Path C)' },
    ],
  },
  {
    key: 'validate',
    title: 'Validate',
    subtitle: 'Check your books',
    type: 'validate',
    progressKey: 'validate',
    reportLinks: [
      { to: '/trialbalance/', label: 'Trial balance (from onboarding date)' },
      { to: '/balancesheet/', label: 'Balance sheet' },
      { to: '/membersummaryreport/', label: 'Member summary' },
    ],
  },
  {
    key: 'complete',
    title: 'Finish',
    subtitle: 'Go live',
    type: 'complete',
    progressKey: 'complete',
  },
];

export function getStepStatusIcon(status) {
  switch (status) {
    case 'done':
      return 'finish';
    case 'skipped':
      return 'skip';
    case 'optional':
      return 'wait';
    default:
      return 'process';
  }
}

export function skippedStepsStorageKey(saccoId) {
  return `onboarding_skipped_${saccoId}`;
}

export function loadSkippedSteps(saccoId) {
  try {
    const raw = localStorage.getItem(skippedStepsStorageKey(saccoId));
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

export function saveSkippedStep(saccoId, stepKey) {
  const current = loadSkippedSteps(saccoId);
  if (!current.includes(stepKey)) {
    current.push(stepKey);
    localStorage.setItem(skippedStepsStorageKey(saccoId), JSON.stringify(current));
  }
  return current;
}
